
import { motion } from 'framer-motion';
import { SeoHead } from '@/components/seo/SeoHead';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { speaker } from '@/data/speaker';

const formation = [
  {
    degree: 'Análise e Desenvolvimento de Sistemas',
    school: 'FATEC Franca',
    desc: 'Formação completa em desenvolvimento de software, linguagens de programação, banco de dados e métodos ágeis.',
  },
  {
    degree: 'Bacharelado em Administração de Empresas',
    school: 'Estácio',
    desc: 'Gestão empresarial com ênfase em empreendedorismo e inovação — visão de negócios que complementa a atuação em tecnologia.',
  },
];

const skills = [
  { area: 'Estratégia de IA', items: ['Automação de processos', 'Implementação em 90 dias', 'ROI mensurável', 'Método PPA'] },
  { area: 'Desenvolvimento', items: ['SaaS & produtos digitais', 'APIs & integrações', 'WhatsApp Business', 'GS1 Digital Link'] },
  { area: 'Educação', items: ['Mentoria individual', 'Cursos online', 'Builder não-técnico', 'Plataforma white-label'] },
];

const AboutPage = () => {
  return (
    <>
      <SeoHead
        route="/about"
        lang="pt-BR"
        title="Sobre — Johnny Vaz"
        description="Estrategista de IA para negócios. Criador do método PPA, da Mentoria IA para Negócios e do curso Builder. 20+ anos entre tecnologia e empreendedorismo."
      />
      <Navbar />

      {/* Hero split */}
      <section style={{ backgroundColor: '#0B0B0B', paddingTop: '7rem', paddingBottom: '4rem' }}>
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            {/* Texto */}
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1.25rem' }}>
                Sobre · Johnny Vaz
              </p>
              <h1 style={{ fontFamily: "'Anton', 'Archivo Black', sans-serif", fontSize: 'clamp(3rem, 8vw, 5.5rem)', lineHeight: 0.88, letterSpacing: '-0.01em', textTransform: 'uppercase', color: '#F2EDE3', marginBottom: '2rem' }}>
                ESTRATEGISTA<br />
                DE IA PARA<br />
                <span style={{ color: '#FF4500' }}>NEGÓCIOS</span>
              </h1>
              <div style={{ height: '2px', backgroundColor: '#FF4500', width: '64px', marginBottom: '2rem' }} />
              <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: 'clamp(1rem, 2vw, 1.15rem)', lineHeight: 1.7, color: '#8C8377', maxWidth: '500px' }}>
                20+ anos entre tecnologia e empreendedorismo. Criador do método PPA,
                da Mentoria IA para Negócios, do Builder e de produtos como BuscaRápida e Cod2D.
                Passou por Magazine Luiza, venceu hackathon do Magalu e Startup Weekend.
              </p>
            </motion.div>

            {/* Foto */}
            <motion.div
              className="hidden md:block flex-shrink-0"
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              style={{ width: 'clamp(220px, 25vw, 360px)', position: 'relative' }}
            >
              <div style={{ position: 'absolute', top: '10%', right: '-12px', width: '3px', height: '80%', backgroundColor: '#FF4500' }} />
              <img
                src="/images/johnny/06-confiante-navy.png"
                alt="Johnny Vaz"
                style={{ width: '100%', display: 'block' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      <main>
        {/* Bio */}
        <section style={{ backgroundColor: '#F2EDE3', padding: '5rem 0' }}>
          <div className="container mx-auto px-6 max-w-4xl">
            <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1rem' }}>
              Trajetória
            </p>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '1.05rem', lineHeight: 1.75, color: '#1A1916', marginBottom: '1rem' }}>
                  Comecei com suporte técnico, passei por desenvolvimento de ERPs e web, e cheguei à consultoria estratégica de IA. Cada etapa foi uma escola diferente — de programação à gestão, de times a parcerias.
                </p>
                <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '1.05rem', lineHeight: 1.75, color: '#1A1916', marginBottom: '1rem' }}>
                  Hoje atuo com empresários que faturam R$ 50k+/mês e querem implementar IA de verdade — não em teoria. E com founders não-técnicos que constroem com IA e precisam saber o que não sabem.
                </p>
                {/* Autoridade em IA — fatos da bio oficial (docs/JOHNNYVAZ/BIO.md). */}
                <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '1.05rem', lineHeight: 1.75, color: '#1A1916' }}>
                  Em IA, lidero projetos na {speaker.employer.name} aplicados a análise de dados e automação de processos, com experiência em arquitetura de sistemas, desenvolvimento fullstack e IA generativa em produção. Pela{' '}
                  <a href={speaker.org.url} style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>{speaker.org.name}</a>, levo treinamento de IA ao vivo às equipes nas empresas: escrever os próprios pedidos, montar contexto e questionar a resposta, com LGPD desde o início. Escrevo sobre isso em{' '}
                  {speaker.aiWriting.map((article, i) => (
                    <span key={article.url}>{i > 0 && ', '}<a href={article.url} style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>{article.title}</a></span>
                  ))}.
                </p>
              </div>
              <div>
                <blockquote style={{ fontFamily: "'Instrument Serif', 'Times New Roman', serif", fontStyle: 'italic', fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)', lineHeight: 1.45, color: '#0B0B0B', borderLeft: '3px solid #FF4500', paddingLeft: '1.25rem', marginBottom: '2rem' }}>
                  "Não precisamos de mais ferramentas de IA. Precisamos de pessoas que sabem o que fazer com elas."
                </blockquote>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {['Estratégia de IA', 'Mentoria', 'Desenvolvimento de Produtos', 'Empreendedorismo'].map((tag) => (
                    <span key={tag} style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C8377', backgroundColor: 'rgba(11,11,11,0.07)', padding: '4px 10px' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Áreas de atuação */}
        <section style={{ backgroundColor: '#0B0B0B', padding: '5rem 0' }}>
          <div className="container mx-auto px-6 max-w-4xl">
            <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1rem' }}>
              Áreas de Atuação
            </p>
            <h2 style={{ fontFamily: "'Anton', 'Archivo Black', sans-serif", fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 0.92, letterSpacing: '-0.01em', textTransform: 'uppercase', color: '#F2EDE3', marginBottom: '0.75rem' }}>
              O QUE<br /><span style={{ color: '#FF4500' }}>FAÇO</span>
            </h2>
            <div style={{ height: '2px', backgroundColor: '#FF4500', width: '64px', marginBottom: '3rem' }} />
            <div className="grid md:grid-cols-3 gap-1">
              {skills.map((s, i) => (
                <div key={s.area} style={{ backgroundColor: i % 2 === 1 ? 'rgba(242,237,227,0.04)' : 'transparent', border: '1px solid rgba(242,237,227,0.1)', padding: '2rem' }}>
                  <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1rem' }}>
                    {s.area}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {s.items.map((item) => (
                      <li key={item} style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '0.9rem', color: '#8C8377', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <span style={{ color: '#FF4500', marginTop: '0.1rem' }}>→</span>{item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Formação */}
        <section style={{ backgroundColor: '#F2EDE3', padding: '5rem 0' }}>
          <div className="container mx-auto px-6 max-w-4xl">
            <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1rem' }}>
              Formação Acadêmica
            </p>
            <h2 style={{ fontFamily: "'Anton', 'Archivo Black', sans-serif", fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 0.92, letterSpacing: '-0.01em', textTransform: 'uppercase', color: '#0B0B0B', marginBottom: '0.75rem' }}>
              BASE<br /><span style={{ color: '#FF4500' }}>ACADÊMICA</span>
            </h2>
            <div style={{ height: '2px', backgroundColor: '#FF4500', width: '64px', marginBottom: '3rem' }} />
            <div className="grid md:grid-cols-2 gap-1">
              {formation.map((f) => (
                <div key={f.degree} style={{ border: '1px solid rgba(11,11,11,0.12)', padding: '2rem' }}>
                  <h3 style={{ fontFamily: "'Anton', 'Archivo Black', sans-serif", fontSize: '1.3rem', lineHeight: 1, textTransform: 'uppercase', color: '#0B0B0B', marginBottom: '0.5rem' }}>
                    {f.degree}
                  </h3>
                  <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1rem' }}>
                    {f.school}
                  </p>
                  <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '0.9rem', lineHeight: 1.6, color: '#1A1916' }}>
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AboutPage;
